import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BillingPlan,
  BillingService,
  BillingSubscriptionResponse,
  FormPack,
} from '../../../core/services/billing.service';
import { LoggerService } from '../../../core/services/logger.service';
import { ToastService } from '../../../core/services/toast.service';
import { TranslationService } from '../../../core/services/translation.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { ChipComponent } from '../../../shared/components/chip/chip.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-billing-settings',
  standalone: true,
  imports: [ButtonComponent, CardComponent, ChipComponent, SpinnerComponent, TranslatePipe],
  templateUrl: './billing-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './billing-settings.component.css',
})
export class BillingSettingsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private billingService = inject(BillingService);
  private toast = inject(ToastService);
  private translate = inject(TranslationService);
  private logger = inject(LoggerService).getLogger('billing-settings');

  state = signal<'loading' | 'loaded' | 'error'>('loading');
  subscription = signal<BillingSubscriptionResponse | null>(null);
  otherPlans = signal<BillingPlan[]>([]);
  formPacks = signal<FormPack[]>([]);
  actionInFlight = signal(false);

  ngOnInit(): void {
    const checkout = this.route.snapshot.queryParamMap.get('checkout');
    if (checkout === 'cancelled') {
      this.toast.info(this.translate.translate('billingSettings.checkoutCancelled'));
    }

    this.load();
  }

  private load(): void {
    this.state.set('loading');

    this.billingService.getSubscription().subscribe({
      next: (subscription) => {
        this.subscription.set(subscription);

        this.billingService.getPlans().subscribe({
          next: (response) => {
            this.otherPlans.set(
              response.plans.filter((plan) => plan.selfServe && plan.id !== subscription.plan.id),
            );
            this.formPacks.set(response.formPacks);
            this.state.set('loaded');
          },
          error: (err) => {
            this.logger.error('Failed to load plan catalog:', err);
            this.state.set('error');
          },
        });
      },
      error: (err) => {
        this.logger.error('Failed to load subscription:', err);
        this.state.set('error');
      },
    });
  }

  formatPrice(cents: number | null): string {
    if (cents === null) {
      return '';
    }
    if (cents === 0) {
      return '$0/mo';
    }
    return `$${(cents / 100).toFixed(0)}/mo`;
  }

  usagePercent(used: number, limit: number): number {
    if (limit < 0) {
      return 0; // unlimited
    }
    if (limit === 0) {
      return 100;
    }
    return Math.min(100, Math.round((used / limit) * 100));
  }

  upgradeToPlan(plan: BillingPlan): void {
    if (this.actionInFlight()) {
      return;
    }
    this.actionInFlight.set(true);

    this.billingService
      .createCheckoutSession({
        type: 'subscription',
        planId: plan.id,
        successPath: '/settings/billing',
        cancelPath: '/settings/billing',
      })
      .subscribe({
        next: (response) => {
          window.location.href = response.url;
        },
        error: (err) => {
          this.logger.error('Failed to start checkout:', err);
          this.toast.error(this.translate.translate('billingSettings.checkoutError'));
          this.actionInFlight.set(false);
        },
      });
  }

  buyFormPack(pack: FormPack): void {
    if (this.actionInFlight()) {
      return;
    }
    this.actionInFlight.set(true);

    this.billingService
      .createCheckoutSession({
        type: 'form-pack',
        packId: pack.id,
        successPath: '/settings/billing',
        cancelPath: '/settings/billing',
      })
      .subscribe({
        next: (response) => {
          window.location.href = response.url;
        },
        error: (err) => {
          this.logger.error('Failed to start form pack checkout:', err);
          this.toast.error(this.translate.translate('billingSettings.checkoutError'));
          this.actionInFlight.set(false);
        },
      });
  }

  manageBilling(): void {
    if (this.actionInFlight()) {
      return;
    }
    this.actionInFlight.set(true);

    this.billingService.createPortalSession().subscribe({
      next: (response) => {
        window.location.href = response.url;
      },
      error: (err) => {
        this.logger.error('Failed to open billing portal:', err);
        this.toast.error(this.translate.translate('billingSettings.portalError'));
        this.actionInFlight.set(false);
      },
    });
  }
}
